import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import { WarningIconSmall } from '@liveauctioneers/caterwaul-components/lib/Icons';
import Dotdotdot from 'react-dotdotdot';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback2';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import React, { Component } from 'react';
import styled, { css } from 'styled-components';

type Props = {
    id: number;
    imageUrl: string;
    isNew: boolean;
    link: string;
    markNotificationsDeleted: Function;
    markNotificationsRead: Function;
    message: string;
    messageLevel: string;
    timestamp: string;
    title: string;
};

type State = {
    isHovered: boolean;
};

export default class Notification extends Component<Props, State> {
    static defaultProps = {
        id: 0,
        imageUrl: '',
        isNew: false,
        link: '',
        message: '',
        messageLevel: '',
        timestamp: '',
        title: '',
    };
    state = { isHovered: false };

    markDeleted = () => {
        this.props.markNotificationsDeleted([this.props.id]);
    };

    markDeletedWithoutNavigation = (event: any) => {
        event.preventDefault();
        this.props.markNotificationsDeleted([this.props.id]);
    };

    markRead = () => {
        this.props.markNotificationsRead([this.props.id]);
    };

    markReadWithoutNavigation = (event: any) => {
        event.preventDefault();
        this.props.markNotificationsRead([this.props.id]);
    };

    hover = () => {
        this.setState({ isHovered: true });
    };

    leave = () => {
        this.setState({ isHovered: false });
    };

    render() {
        const { imageUrl, isNew, link, message, messageLevel, timestamp, title } = this.props;

        let titleToUse: any = title;
        const target = link.toLowerCase().includes('console') ? '_blank' : '_self';
        const route = link.toLowerCase().includes('item') || link.toLowerCase().includes('catalog');
        const newLabel = isNew ? (
            <>
                <StyledNewDot>{'\u2022'}</StyledNewDot>
                <span>New |&nbsp;</span>
            </>
        ) : null;

        const timestampDisplay = timestamp.match(/less than a minute/) ? (
            <FormattedMessage id="notifications.just_now" />
        ) : (
            timestamp
        );
        const approvedMatch = title.match(/Approved by (.*)/i);
        const declinedMatch = title.match(/Declined by (.*)/i);
        let houseName;
        if (approvedMatch) {
            titleToUse = (
                <StyledApprovedContainer>
                    <StyledFontAwesomeIcon icon={faCheckCircle} />
                    <FormattedMessage id="registration_approved" />
                </StyledApprovedContainer>
            );
            houseName = approvedMatch[1];
        } else if (declinedMatch) {
            titleToUse = (
                <StyledDeniedContainer>
                    <WarningIconSmall color="red" />
                    <FormattedMessage id="registration_declined" />
                </StyledDeniedContainer>
            );
            houseName = declinedMatch[1];
        }

        return (
            <StyledNotificationContainer $isNew={isNew} onMouseEnter={this.hover} onMouseLeave={this.leave}>
                <StyledNotificationLink rel="noopener" route={route} target={target} to={link}>
                    <StyledMessageContainer>
                        <StyleImageContainer>
                            <StyledImageWithFallback alt="Item" src={imageUrl} />
                        </StyleImageContainer>
                        <StyledText>
                            <StyledTitle $messageLevel={messageLevel}>{titleToUse}</StyledTitle>
                            <StyledMessageBody>
                                {houseName}
                                {Boolean(houseName) && <br />}
                                <Dotdotdot clamp={2}>{message}</Dotdotdot>
                            </StyledMessageBody>
                            <StyledTimeStamp $isNew={isNew}>
                                {newLabel}
                                <span>{timestampDisplay}</span>
                            </StyledTimeStamp>
                        </StyledText>
                        <StyledCloseButton onClick={this.markDeletedWithoutNavigation}>
                            <span>
                                <FontAwesomeIcon icon={faTimes} />
                            </span>
                        </StyledCloseButton>
                    </StyledMessageContainer>
                    <hr />
                </StyledNotificationLink>
            </StyledNotificationContainer>
        );
    }
}

const StyledNewDot = styled.span`
    color: ${({ theme }) => theme.colors.red100};
    font-size: 20px;
    line-height: 18px;
    padding-right: 3px;
`;

const StyledApprovedContainer = styled.span`
    color: ${({ theme }) => theme.colors.teal};
`;

const StyledDeniedContainer = styled.span`
    color: ${({ theme }) => theme.colors.red100};
    display: inline-flex;
    align-items: center;
    svg {
        margin-right: 4px;
    }
`;

const StyledNotificationContainer = styled.div<{ $isNew?: boolean }>`
    display: block;

    hr {
        margin: 0;
    }

    ${({ $isNew }) => {
        if ($isNew) {
            return css`
                background-color: ${({ theme }) => theme.colors.grey500};
            `;
        }
    }}
`;

const StyledNotificationLink = styled(Link)`
    display: block;
    height: 100%;
    text-decoration: none;
    color: ${({ theme }) => theme.colors.black};
    cursor: pointer;

    &:visited {
        text-decoration: none;
        color: ${({ theme }) => theme.colors.black};
    }
`;

const StyledMessageContainer = styled.div`
    display: flex;
    height: 100%;
    position: relative;

    &:hover .close-button {
        color: ${({ theme }) => theme.colors.grey300};
    }
`;

const StyleImageContainer = styled.div`
    background-color: ${({ theme }) => theme.colors.white};
    height: 104px;
    min-height: 104px;
    width: 104px;
    min-width: 104px;
    padding: 10px;
    margin: auto 0;
`;

const StyledImageWithFallback = styled(ImageWithFallback)`
    object-fit: contain;
    height: 100%;
    width: 100%;
    margin: auto;
`;

const StyledText = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 calc(100% - 110px);
    max-width: calc(100% - 110px);
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    margin: 5px 20px 3px 10px;
    width: 228px;
`;

const StyledTitle = styled.div<{ $messageLevel?: string }>`
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
    font-size: 15px;
    line-height: 24px;
    height: 24px;
    letter-spacing: 0.5px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    overflow-wrap: break-word;
    ${({ $messageLevel }) => {
        if ($messageLevel === 'urgent') {
            return css`
                color: ${({ theme }) => theme.colors.red100};
            `;
        }
    }}
`;

const StyledMessageBody = styled.div`
    font-size: 15px;
    font-family: ${({ theme }) => theme.typography.fontSecondary};
    line-height: 24px;
    letter-spacing: 0.5px;
    text-overflow: ellipsis;
    overflow: hidden;
    max-height: 48px;
    margin-top: -1px;
`;

const StyledTimeStamp = styled.div<{ $isNew?: boolean }>`
    color: ${({ theme }) => theme.colors.grey100};
    display: flex;
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    font-size: 14px !important;
    letter-spacing: 0.5px;
    margin: auto 0 2px;
    ${({ $isNew }) => {
        if ($isNew) {
            return css`
                color: ${({ theme }) => theme.typography.fontPrimaryStrong};
            `;
        }
    }}
`;

const StyledCloseButton = styled.div`
    color: transparent;
    top: 5px;
    right: 8px;
    position: absolute;
    cursor: pointer;

    &:hover {
        color: ${({ theme }) => theme.colors.grey100} !important;
    }
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
    height: 15px;
    width: 15px;
    flex: 0 0 auto;
    margin-right: 7px;
`;
